import { useEffect, useMemo, useState } from "react";
import useAxios from "../../utils/useAxios";
import { useRef } from "react";
import AsyncSelect from "react-select/async";
import { useAppContext } from "../../context/AppContext";

function DonationTab({ donor_id }) {
  // console.log("Current Donor id", donor_id);

  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [sbiDonations, setSbiDonations] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [role, setRole] = useState(null);
  const [view, setView] = useState("excel"); // default: show Excel section
  const [excelFile, setExcelFile] = useState(null);
  const [donorDetails, setDonorDetails] = useState([]);
  const [message, setMessage] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [uploadRows, setUploadRows] = useState([]);
  const axiosAPI = useAxios();
  const fileInputRef = useRef();
  const { state } = useAppContext();
  const { data, loading: donorLoading } = state;

  const fetchDonationById = async () => {
    try {
      setLoading(true);
      const response = await axiosAPI.get(
        `/api/donor/get-donation-by-id/${donor_id}`
      );
      // console.log(response.data.data);
      setSbiDonations(response.data.data); // Assuming backend returns { data: [...] }
      //   setError("");
    } catch (err) {
      console.error("Error fetching donor gifts:", err);
      //   setError("Failed to fetch gifts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (donor_id) {
      fetchDonationById();
    }
  }, [donor_id]);

  const [formData, setFormData] = useState({
    user_id: "",
    transaction_date_and_time: "",
    amount: "",
    tr_no: "",
  });

  const [trError, setTrError] = useState("");
  const [isCheckingTr, setIsCheckingTr] = useState(false);

  // Debounced check for TR Number availability
  useEffect(() => {
    const trNo = formData.tr_no ? formData.tr_no.trim() : "";
    if (!trNo) {
      setTrError("");
      setIsCheckingTr(false);
      return;
    }

    setIsCheckingTr(true);
    const timer = setTimeout(async () => {
      try {
        const response = await axiosAPI.get(
          `/api/donation/check-tr-number?tr_no=${encodeURIComponent(trNo)}`
        );
        if (response.data && response.data.exists) {
          setTrError(`TR number "${trNo}" already exists!`);
        } else {
          setTrError("");
        }
      } catch (err) {
        console.error("Error checking TR number:", err);
      } finally {
        setIsCheckingTr(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [formData.tr_no]);

  const filteredOptions = useMemo(() => {
    return data
      .filter((user) => {
        const nameMatch = user.full_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase());
        const phoneMatch = user.phone_number
          ?.toString()
          .includes(searchTerm.toLowerCase());
        return nameMatch || phoneMatch;
      })
      .slice(0, 50)
      .map((user) => ({
        value: user.id,
        label: `${user.full_name} - ${user.phone_number || ""}`,
      }));
  }, [data, searchTerm]);

  const loadOptions = (inputValue, callback) => {
    setSearchTerm(inputValue);
    callback(filteredOptions);
  };

  // console.log("Filter Option", filteredOptions);

  useEffect(() => {
    if (formData.user_id) {
      // filteredOptions is your array of options or you can fetch full user info
      const option =
        filteredOptions.find((opt) => opt.value === formData.user_id) || null;
      setSelectedUser(option);
    } else {
      setSelectedUser(null);
    }
  }, [formData.user_id, filteredOptions]);

  const handleExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (
      file &&
      file.type !==
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      alert("Please upload a valid Excel file (.xlsx)");
      setExcelFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = null; // Reset input field
      }
    } else {
      setExcelFile(file);
    }
  };

  const handleExcelSubmit = async (e) => {
    e.preventDefault();

    if (!excelFile) {
      alert("Please select an Excel file.");
      return;
    }

    const formData = new FormData();
    formData.append("excel", excelFile);

    try {
      const response = await axiosAPI.post(
        "/api/donation/upload-excel",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const {
        message = "Upload completed",
        insertedCount = 0,
        rows = [],
      } = response.data;

      // Set upload rows for summary table (if needed in UI)
      setUploadRows && setUploadRows(rows);

      const failedRows = rows.filter((r) => r.status === "fail");
      const passedRows = rows.filter((r) => r.status === "pass");

      // console.log("Upload Summary:", {
      //   insertedCount,
      //   passed: passedRows.length,
      //   failed: failedRows.length,
      // });

      //  Fix: Check if rows were even processed
      if (rows.length === 0) {
        window.notifyMessage(
          "Error",
          "No valid data found in Excel file.",
          "error"
        );
      } else if (insertedCount === 0 && failedRows.length > 0) {
        window.notifyMessage(
          "Upload Failed",
          `No rows were inserted.\nFailed Rows: ${failedRows.length}`,
          "error"
        );
        console.table(failedRows);
      } else if (failedRows.length > 0) {
        window.notifyMessage(
          "Partial Success",
          `${message}\nInserted: ${insertedCount}, Failed: ${failedRows.length}`,
          "warning"
        );
        console.table(failedRows);
      } else {
        window.notifyMessage(
          "Success",
          "Excel file uploaded successfully!",
          "success"
        );
      }

      // Close modal and reset form
      // closeModal();
      // hideModal("newGiftExcelModal");

      // Clear input and state
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }

      setExcelFile(null);
      setMessage("");
    } catch (err) {
      console.error("Excel Upload Error:", err);

      const errorMsg =
        err?.response?.data?.message ||
        "Something went wrong while uploading the Excel file.";

      window.notifyMessage("Error", errorMsg, "error");

      if (err.response?.data?.error) {
        console.error("Backend Error:", err.response.data.error);
      }
    }
  };

  useEffect(() => {
    const role = JSON.parse(localStorage.getItem("user_role")); // Parses string "admin" to plain string

    setRole(role);
  }, []);

  // Get All Donor Details
  const getDonorDetails = async () => {
    try {
      const response = await axiosAPI.get("/api/donor/get-donor-details");
      setDonorDetails(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  function fetchCurrentDateAndTime() {
    const now = new Date();

    // Manually format the date and time to "YYYY-MM-DD HH:MM"
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0"); // Month is 0-indexed, so we add 1
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");

    // Combine the date and time into the desired format
    const formattedDateTime = `${year}-${month}-${day} ${hours}:${minutes}`;

    setFormData((prev) => ({
      ...prev,
      transaction_date_and_time: formattedDateTime,
    }));
  }

  // Set current date and time when the form loads
  useEffect(() => {
    fetchCurrentDateAndTime();
    getDonorDetails();
  }, []);

  // Handle Input Changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handlePageClick = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setPage(pageNum);
    }
  };

  // Handle Submit (Add or Edit)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (trError) {
      window.notifyMessage("Error", trError, "danger");
      return;
    }

    const payload = {
      user_id: formData.user_id?.value,
      transaction_date_and_time: formData.transaction_date_and_time,
      amount: parseInt(formData.amount, 10), //  ensures INT for DB
      tr_no: formData.tr_no,
    };

    // console.log(payload);

    // return;

    try {
      let response;
      if (isEditMode) {
        response = await axiosAPI.put(
          `/api/donation/update-donation/${currentUserId}`,
          payload
        );
        window.notifyMessage(
          "Success",
          "Donation updated successfully",
          "success"
        );
        //  Local state update kro
        setSbiDonations((prevDonations) =>
          prevDonations.map((donation) =>
            donation.id === currentUserId
              ? {
                  ...donation,
                  user_id: formData.user_id,
                  transaction_date_and_time: formData.transaction_date_and_time,
                  amount: formData.amount,
                  tr_no: formData.tr_no,
                }
              : donation
          )
        );
      } else {
        response = await axiosAPI.post("/api/donation/add-donation", payload);
        window.notifyMessage(
          "Success",
          "Donation added successfully",
          "success"
        );
      }
      hideModal("newUserModal");
      // getNotifications();
      // fetchCurrentDateAndTime()

      setIsEditMode(false);
      // window.location.reload(); // Reload page to reflect changes in the notification list
      closeModal();
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        window.notifyMessage("Error", error.response.data.message, "danger");
      } else {
        window.notifyMessage("Error", "There was an error!", "danger");
      }
    }
  };

  const openUploadDonorModal = () => {
    showModal("newUserExcelModal");
  };

  // const handleExportDonations = () => {
  //   if (filters.search || (filters.from_date && filters.to_date)) {
  //     // Proceed with export
  //     downloadExcel("donations", filters); // Pass filters if needed
  //   } else {
  //     alert("Please select both From and To dates or enter a search term.");
  //   }
  // };

  const openEditModal = (id) => {
    setIsEditMode(true);
    setCurrentUserId(id);
    // Find the notification with this ID and populate the form
    const selected_donation = sbiDonations.find((donor) => donor.id === id);

    setCurrentUserId(id);
    // console.log(selected_donation);

    if (selected_donation) {
      const selectedUser = data.find(
        (user) => user.id === selected_donation.user_id
      );
      // console.log("Select User", selectedUser);
      setFormData({
        // user_id: selected_donation.user_id,
        user_id: selectedUser
          ? { value: selectedUser?.id, label: selectedUser?.full_name }
          : null,
        transaction_date_and_time: selected_donation.transaction_date_and_time,
        amount: selected_donation.amount,
        tr_no: selected_donation.tr_no,
      });
    }

    showModal("newUserModal");
  };

  // Close Modal
  const closeModal = () => {
    setIsEditMode(false);
    setUploadRows("");
    setCurrentUserId(null);
  };

  // Helper functions to manage modal visibility
  function hideModal(id) {
    const modal_el = document.querySelector("#" + id);
    const modal_obj = window.bootstrap.Modal.getInstance(modal_el);

    if (modal_obj == null) {
      return;
    }

    modal_obj.hide();
  }

  function showModal(id) {
    const modal_el = document.querySelector("#" + id);
    let modal_obj = window.bootstrap.Modal.getInstance(modal_el);

    if (modal_obj == null) {
      modal_obj = new window.bootstrap.Modal(modal_el, {
        backdrop: "static",
      });
    }

    modal_obj.show();
  }

  const openAddModal = () => {
    showModal("newUserModal");
    fetchCurrentDateAndTime();
  };
  return (
    <div>
      <div className="page-inner">
        {/* Modal */}
        <div
          className="modal fade"
          id="newUserModal"
          tabIndex="-1"
          aria-labelledby="newUserModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="newUserModalLabel">
                  {isEditMode ? "Edit Donation" : "Add Donation"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  {/* Donor Select */}
                  <div className="mb-3">
                    <label htmlFor="user_id" className="form-label">
                      Select Donor
                    </label>
                    <AsyncSelect
                      cacheOptions
                      defaultOptions={filteredOptions}
                      loadOptions={loadOptions}
                      onChange={(selectedOption) =>
                        setFormData((prev) => ({
                          ...prev,
                          user_id: selectedOption,
                        }))
                      }
                      value={formData.user_id}
                      isClearable
                      placeholder="-- Select Donor ---"
                    />
                  </div>

                  {/* Date & Time */}
                  <div className="mb-3">
                    <label
                      htmlFor="transaction_date_and_time"
                      className="form-label"
                    >
                      Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      id="transaction_date_and_time"
                      name="transaction_date_and_time"
                      value={formData.transaction_date_and_time}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* Amount */}
                  <div className="mb-3">
                    <label htmlFor="amount" className="form-label">
                      Amount
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="amount"
                      name="amount"
                      placeholder="Enter Donation Amount"
                      value={formData.amount}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* TR No */}
                  <div className="mb-3">
                    <label htmlFor="tr_no" className="form-label">
                      TR No
                    </label>
                    <input
                      type="text"
                      className={`form-control ${trError ? "is-invalid" : ""}`}
                      id="tr_no"
                      name="tr_no"
                      placeholder="Enter tr number"
                      value={formData.tr_no}
                      onChange={handleChange}
                    />
                    {isCheckingTr && (
                      <small className="text-primary d-block mt-1">
                        Checking TR number availability...
                      </small>
                    )}
                    {trError && (
                      <small className="text-danger d-block mt-1 fw-bold">
                        {trError}
                      </small>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-round btn-sm"
                  >
                    {isEditMode ? "Update  Donation" : "Add  Donation"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Donation Table */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex flex-wrap justify-content-between align-items-end gap-3">
                {/* Left: Title */}
                <h4 className="card-title mb-0">
                  All Manual Donations ({sbiDonations.length})
                </h4>
              </div>

              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Mode</th>
                        <th>Donation Type</th>
                        {/* <th>Agency Name</th> */}
                        <th>Amount</th>
                        <th>Tr No</th>
                        <th>Date</th>
                        {/* <th>Subscription_Expiry_Date</th> */}
                        {/* <th>Created At</th> */}
                        {/* <tr>
                          {role === "admin" && (
                            <th>Action</th> // Show this if the role is "admin"
                          )}
                        </tr> */}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="text-center">
                            <div
                              style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 9999,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <div
                                className="spinner-border text-success"
                                role="status"
                                style={{ width: "3rem", height: "3rem" }}
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : sbiDonations.length == 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No Data Found
                          </td>
                        </tr>
                      ) : (
                        sbiDonations.map((sbi) => {
                          const formattedDate = new Date(
                            sbi.transaction_date_and_time
                          ).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          });

                          // Check if the message length is greater than 50 characters
                          //   const truncatedMessage =
                          //     sbi.sbiication_message.length > 50
                          //       ? sbi.sbiication_message.slice(0, 50) + "..."
                          //       : sbi.sbiication_message;

                          return (
                            <tr key={sbi.id}>
                              <td>{sbi?.full_name}</td>
                              <td>{sbi?.mode}</td>
                              <td>
                                {sbi?.donation_type
                                  ?.split("_")
                                  ?.join(" ")
                                  ?.toUpperCase()}
                              </td>
                              {/* <td>{sbi?.processing_agency}</td> */}
                              <td>{sbi?.amount}</td>
                              <td>{sbi?.tr_no}</td>
                              <td>{formattedDate}</td>
                              {/* <td>{formattedDate}</td> */}
                              {/* <td>{truncatedMessage}</td> */}
                              {/* <td>
                                {role === "admin" && (
                                  <button
                                    className="btn btn-icon btn-round btn-dark btn-sm me-2"
                                    onClick={() => openEditModal(sbi.id)}
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                )}
                                <button
                                  className="btn btn-icon btn-round btn-danger btn-sm me-2"
                                  onClick={() => handleDelete(sbi.id)}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                                {role === "admin" && (
                                  <button
                                    className="btn btn-icon btn-round btn-danger btn-sm me-2"
                                    onClick={() => handleDelete(sbi.id)}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                )}
                              </td> */}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <nav aria-label="Page navigation">
              <ul className="pagination flex justify-content-end">
                {/* First Page */}
                <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                  >
                    First
                  </button>
                </li>

                {/* Previous */}
                <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(page - 1)}
                  >
                    Previous
                  </button>
                </li>

                {/* Page Number Buttons in chunks */}
                {(() => {
                  const pageNumbers = [];
                  const maxPagesToShow = 5;
                  const startPage =
                    Math.floor((page - 1) / maxPagesToShow) * maxPagesToShow +
                    1;
                  const endPage = Math.min(
                    startPage + maxPagesToShow - 1,
                    totalPages
                  );

                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <li
                        key={i}
                        className={`page-item ${page === i ? "active" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageClick(i)}
                        >
                          {i}
                        </button>
                      </li>
                    );
                  }
                  return pageNumbers;
                })()}

                {/* Next */}
                <li
                  className={`page-item ${
                    page === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(page + 1)}
                  >
                    Next
                  </button>
                </li>

                {/* Last Page */}
                <li
                  className={`page-item ${
                    page === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    Last
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DonationTab;
